import { ErrorCode, OpenFeatureEventEmitter, ProviderStatus, GeneralError, ProviderEvents, StandardResolutionReasons } from '@openfeature/web-sdk';
import isEmpty from 'lodash.isempty';
import { basicLogger, initialize } from 'launchdarkly-js-client-sdk';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
//Copyright 2022 Catamorphic, Co.
//
//  Licensed under the Apache License, Version 2.0 (the "License");
//you may not use this file except in compliance with the License.
//  You may obtain a copy of the License at
//
//http://www.apache.org/licenses/LICENSE-2.0
//
//  Unless required by applicable law or agreed to in writing, software
//distributed under the License is distributed on an "AS IS" BASIS,
//  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//  See the License for the specific language governing permissions and
//limitations under the License.
const LDContextBuiltIns = {
    name: 'string',
    anonymous: 'boolean',
};
/**
 * Convert attributes, potentially recursively, into appropriate types.
 * @param logger Logger to use if issues are encountered.
 * @param key The key for the attribute.
 * @param value The value of the attribute.
 * @param object Object to place the value in.
 * @param visited Carry visited keys of the object
 */
function convertAttributes(logger, key, value, object, visited) {
    if (visited.includes(value)) {
        // Prevent cycles by not visiting the same object
        // with in the same branch. Different branches
        // may contain the same object.
        logger.error('Detected a cycle within the evaluation context. The ' +
            'affected part of the context will not be included in evaluation.');
        return;
    }
    // This method is recursively populating objects, so we are intentionally
    // re-assigning to a parameter to prevent generating many intermediate objects.
    if (value instanceof Date) {
        // eslint-disable-next-line no-param-reassign
        object[key] = value.toISOString();
    }
    else if (typeof value === 'object' && !Array.isArray(value)) {
        // eslint-disable-next-line no-param-reassign
        object[key] = {};
        Object.entries(value).forEach(([objectKey, objectValue]) => {
            convertAttributes(logger, objectKey, objectValue, object[key], [...visited, value]);
        });
    }
    else {
        // eslint-disable-next-line no-param-reassign
        object[key] = value;
    }
}
/**
 * Translate the common part of a context. This could either be the attributes
 * of a single context, or it could be the attributes of a nested context
 * in a multi-context.
 * @param logger Logger to use if issues are encountered.
 * @param inCommon The source context information. Could be an EvaluationContext, or a value
 * within an Evaluation context.
 * @param inTargetingKey The targetingKey, either it or the key may be used.
 * @returns A populated common context.
 */
function translateContextCommon(logger, inCommon, inTargetingKey) {
    const keyAttr = inCommon['key'];
    const finalKey = inTargetingKey !== null && inTargetingKey !== undefined ? inTargetingKey : keyAttr;
    if (keyAttr != null && inTargetingKey != null) {
        logger.warn("The EvaluationContext contained both a 'targetingKey' and a 'key' attribute. The" +
            " 'key' attribute will be discarded.");
    }
    if (finalKey == null) {
        logger.error("The EvaluationContext must contain either a 'targetingKey' or a 'key' and the " + 'type must be a string.');
    }
    const convertedContext = { key: finalKey };
    Object.entries(inCommon).forEach(([key, value]) => {
        if (key === 'targetingKey' || key === 'key') {
            return;
        }
        if (key === 'privateAttributes') {
            // eslint-disable-next-line no-underscore-dangle
            convertedContext._meta = {
                privateAttributes: value,
            };
        }
        else if (key in LDContextBuiltIns) {
            const typedKey = key;
            if (typeof value === LDContextBuiltIns[typedKey]) {
                convertedContext[key] = value;
            }
            else {
                // If the type does not match, then discard.
                logger.error(`The attribute '${key}' must be of type ${LDContextBuiltIns[typedKey]}`);
            }
        }
        else {
            convertAttributes(logger, key, value, convertedContext, [inCommon]);
        }
    });
    return convertedContext;
}
/**
 * Convert an OpenFeature evaluation context into an LDContext.
 * @param logger Logger.
 * @param evalContext The OpenFeature evaluation context to translate.
 * @returns An LDContext based on the evaluation context.
 *
 * @internal
 */
function translateContext(logger, evalContext) {
    let finalKind = 'user';
    // A multi-context.
    if (evalContext['kind'] === 'multi') {
        return Object.entries(evalContext).reduce((acc, [key, value]) => {
            if (key === 'kind') {
                acc.kind = value;
            }
            else if (typeof value === 'object' && !Array.isArray(value)) {
                const valueRecord = value;
                acc[key] = translateContextCommon(logger, valueRecord, valueRecord['targetingKey']);
            }
            else {
                logger.error('Top level attributes in a multi-kind context should be Structure types.');
            }
            return acc;
        }, {});
    }
    if (evalContext['kind'] !== undefined && typeof evalContext['kind'] === 'string') {
        // Single context with specified kind.
        finalKind = evalContext['kind'];
    }
    else if (evalContext['kind'] !== undefined && typeof evalContext['kind'] !== 'string') {
        logger.warn("Specified 'kind' of context was not a string.");
    }
    return Object.assign({ kind: finalKind }, translateContextCommon(logger, evalContext, evalContext.targetingKey));
}

function translateErrorKind(errorKind) {
    // Error code specification.
    // https://github.com/open-feature/spec/blob/main/specification/sections/02-providers.md#requirement-28
    switch (errorKind) {
        case 'CLIENT_NOT_READY':
            return ErrorCode.PROVIDER_NOT_READY;
        case 'MALFORMED_FLAG':
            return ErrorCode.PARSE_ERROR;
        case 'FLAG_NOT_FOUND':
            return ErrorCode.FLAG_NOT_FOUND;
        case 'USER_NOT_SPECIFIED':
            return ErrorCode.TARGETING_KEY_MISSING;
        // General errors.
        default:
            return ErrorCode.GENERAL;
    }
}
/**
 * Translate an {@link LDEvaluationDetail} to a {@link ResolutionDetails}.
 * @param result The {@link LDEvaluationDetail} to translate.
 * @returns An equivalent {@link ResolutionDetails}.
 *
 * @internal
 */
function translateResult(result) {
    var _a, _b, _c;
    const resolution = {
        value: result.value,
        variant: (_a = result.variationIndex) === null || _a === undefined ? undefined : _a.toString(),
        reason: (_b = result.reason) === null || _b === undefined ? undefined : _b.kind,
    };
    if (((_c = result.reason) === null || _c === undefined ? undefined : _c.kind) === 'ERROR') {
        resolution.errorCode = translateErrorKind(result.reason.errorKind);
    }
    return resolution;
}

/**
 * Create a ResolutionDetails for an evaluation that produced a type different
 * from the expected type.
 * @param value The default value to populate the ResolutionDetails with.
 * @returns A ResolutionDetails with the default value.
 */
function wrongTypeResult(value) {
    return {
        value,
        reason: StandardResolutionReasons.ERROR,
        errorCode: ErrorCode.TYPE_MISMATCH,
    };
}
class LaunchDarklyClientProvider {
    set status(status) {
        this._status = status;
    }
    get status() {
        return this._status;
    }
    constructor(envKey, _a) {
        var { logger, initializationTimeout } = _a, ldOptions = __rest(_a, ["logger", "initializationTimeout"]);
        this.envKey = envKey;
        this.metadata = {
            name: 'launchdarkly-client-provider',
        };
        this.events = new OpenFeatureEventEmitter();
        /*
         * implement status field/accessor
         * https://openfeature.dev/specification/sections/providers#requirement-242
         * */
        this._status = ProviderStatus.NOT_READY;
        if (logger) {
            this.logger = logger;
        }
        else {
            this.logger = basicLogger({ level: 'info' });
        }
        this.initializationTimeout = initializationTimeout;
        this.ldOptions = Object.assign(Object.assign({}, ldOptions), { logger: this.logger });
    }
    get client() {
        if (!this._client) {
            throw new GeneralError('Provider is not initialized');
        }
        return this._client;
    }
    initialize(context) {
        return __awaiter(this, undefined, undefined, function* () {
            var _a;
            const _context = isEmpty(context) ? { anonymous: true } : this.translateContext(context);
            this._client = initialize(this.envKey, _context, this.ldOptions);
            /*
             * set event listeners to propagate ld events to open feature provider,
             * use LD property streaming, since when enabled you are opting in for straming updates
             * */
            if ((_a = this.ldOptions) === null || _a === undefined ? undefined : _a.streaming) {
                this.setListeners();
            }
            try {
                yield this._client.waitForInitialization(this.initializationTimeout);
                this.status = ProviderStatus.READY;
            }
            catch (_b) {
                this.status = ProviderStatus.ERROR;
            }
        });
    }
    onClose() {
        return this.client.close();
    }
    /** set listeners to LD client and event the correspodent event in the Provider
     * necessary for LD streaming changes
     * */
    setListeners() {
        this.client.on('change', (changeset) => {
            this.events.emit(ProviderEvents.ConfigurationChanged, {
                flagsChanged: Object.keys(changeset),
            });
        });
    }
    onContextChange(oldContext, newContext) {
        return __awaiter(this, undefined, undefined, function* () {
            // update the context on the LaunchDarkly client, this is so it does not have to be checked on each evaluation
            yield this.client.identify(this.translateContext(newContext));
        });
    }
    resolveBooleanEvaluation(flagKey, defaultValue) {
        const res = this.client.variationDetail(flagKey, defaultValue);
        if (typeof res.value === 'boolean') {
            return translateResult(res);
        }
        return wrongTypeResult(defaultValue);
    }
    resolveNumberEvaluation(flagKey, defaultValue) {
        const res = this.client.variationDetail(flagKey, defaultValue);
        if (typeof res.value === 'number') {
            return translateResult(res);
        }
        return wrongTypeResult(defaultValue);
    }
    resolveObjectEvaluation(flagKey, defaultValue) {
        const res = this.client.variationDetail(flagKey, defaultValue);
        if (typeof res.value === 'object') {
            return translateResult(res);
        }
        return wrongTypeResult(defaultValue);
    }
    resolveStringEvaluation(flagKey, defaultValue) {
        const res = this.client.variationDetail(flagKey, defaultValue);
        if (typeof res.value === 'string') {
            return translateResult(res);
        }
        return wrongTypeResult(defaultValue);
    }
    track(trackingEventName, _context, _a) {
        var { value } = _a, details = __rest(_a, ["value"]);
        // The LD Client already has the context form the identify method, so we can omit it here.
        this.client.track(trackingEventName, details, value);
    }
    translateContext(context) {
        return translateContext(this.logger, context);
    }
}

export { LaunchDarklyClientProvider };
